import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, subDays, subMonths, subYears } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  useBirdCertificate,
  useBirdsListing,
  useBreedingList,
  useBalanceSheet,
  useIncomeStatement,
  useInventoryReport,
} from '../hooks/useReports';
import ReportTypeSelector from '../components/reports/ReportTypeSelector';
import BirdCertificateReport from '../components/reports/BirdCertificateReport';
import ReportHeader from '../components/reports/ReportHeader';
import BirdsListingReport from '../components/reports/BirdsListingReport';
import BreedingListReport from '../components/reports/BreedingListReport';
import MutationInventoryReport from '../components/reports/MutationInventoryReport';
import BreedingOutcomesReport from '../components/reports/BreedingOutcomesReport';
import InventoryReportView from '../components/reports/InventoryReportView';
import BalanceSheetReport from '../components/reports/BalanceSheetReport';
import IncomeStatementReport from '../components/reports/IncomeStatementReport';
import { reportOptions, ReportType } from '../components/reports/reportConfig';
import { ReportFilters } from '../services/reports';
import {
  buildBirdCertificateShareUrl,
  buildCertificateFilename,
  downloadBlob,
  openCertificateMailto,
  renderElementToPdfBlob,
} from '../utils/birdCertificateShare';

const REPORT_IDS = new Set(reportOptions.map((r) => r.id));

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [filters, setFilters] = useState<ReportFilters>({});
  const [selectedBirdId, setSelectedBirdId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('all');
  const [pdfBusy, setPdfBusy] = useState(false);

  const pagePrintRef = useRef<HTMLDivElement>(null);
  const certificatePrintRef = useRef<HTMLDivElement>(null);

  const birdCertificate = useBirdCertificate(selectedBirdId);

  // Hydrate from deep link: /reports?report=bird-certificate&birdId=...
  useEffect(() => {
    const reportParam = searchParams.get('report');
    const birdParam = searchParams.get('birdId');
    if (reportParam && REPORT_IDS.has(reportParam as ReportType)) {
      setSelectedReport(reportParam as ReportType);
    }
    if (birdParam) {
      setSelectedBirdId(birdParam);
      if (!reportParam) {
        setSelectedReport('bird-certificate');
      }
    }
  }, [searchParams]);

  // Keep URL in sync for certificate deep links (shareable, auth-gated by app login).
  useEffect(() => {
    if (selectedReport !== 'bird-certificate') return;
    const next = new URLSearchParams();
    next.set('report', 'bird-certificate');
    if (selectedBirdId) next.set('birdId', selectedBirdId);
    setSearchParams(next, { replace: true });
  }, [selectedReport, selectedBirdId, setSearchParams]);

  const birdsListingFilters = useMemo(() => {
    if (selectedReport !== 'birds-listing') return undefined;
    return {
      status: filters.status,
      includeInactive: filters.includeInactive,
    };
  }, [selectedReport, filters.status, filters.includeInactive]);

  const birdsListing = useBirdsListing(birdsListingFilters);

  const breedingListFilters = useMemo(() => {
    if (selectedReport !== 'breeding-list') return undefined;
    return filters;
  }, [selectedReport, filters]);

  const breedingList = useBreedingList(breedingListFilters);

  const incomeStatementFilters = useMemo(() => {
    if (selectedReport !== 'income-statement') return undefined;
    return filters;
  }, [selectedReport, filters]);

  const balanceSheet = useBalanceSheet(selectedReport === 'balance-sheet' ? filters.endDate : undefined);
  const incomeStatement = useIncomeStatement(incomeStatementFilters);
  const inventoryReport = useInventoryReport(selectedReport === 'inventory' ? filters.endDate : undefined);

  const getDateFilters = (range: string = dateRange): ReportFilters => {
    if (range === 'all') return {};

    const now = new Date();
    let startDate: Date;

    switch (range) {
      case '7days':
        startDate = subDays(now, 7);
        break;
      case '30days':
        startDate = subDays(now, 30);
        break;
      case '90days':
        startDate = subDays(now, 90);
        break;
      case '12months':
        startDate = subMonths(now, 12);
        break;
      case 'year':
        startDate = subYears(now, 1);
        break;
      default:
        return {};
    }

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(now, 'yyyy-MM-dd'),
    };
  };

  const handleReportSelect = (reportId: ReportType) => {
    setSelectedReport(reportId);
    setFilters(getDateFilters());
    if (reportId !== 'bird-certificate') {
      setSelectedBirdId(null);
      setSearchParams({}, { replace: true });
    }
  };

  const certificateShareUrl = useMemo(() => {
    if (selectedReport !== 'bird-certificate' || !selectedBirdId) {
      return typeof window !== 'undefined' ? window.location.href : '';
    }
    return buildBirdCertificateShareUrl(selectedBirdId);
  }, [selectedReport, selectedBirdId]);

  const exportCertificatePdf = useCallback(async (): Promise<string | null> => {
    if (!certificatePrintRef.current || !birdCertificate.data) {
      toast.error('Select a bird and wait for the certificate to load before exporting PDF');
      return null;
    }
    setPdfBusy(true);
    try {
      const blob = await renderElementToPdfBlob(certificatePrintRef.current);
      const base = buildCertificateFilename(birdCertificate.data);
      const filename = `${base}.pdf`;
      downloadBlob(blob, filename);
      toast.success('PDF downloaded');
      return filename;
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate PDF');
      return null;
    } finally {
      setPdfBusy(false);
    }
  }, [birdCertificate.data]);

  const handleCertificateEmailShare = useCallback(async () => {
    if (!selectedBirdId || !birdCertificate.data) {
      toast.error('Select a bird certificate first');
      return;
    }
    const filename = await exportCertificatePdf();
    openCertificateMailto({
      title: `Bird Certificate — ${birdCertificate.data.bird.identification.bandId || birdCertificate.data.bird.bandNumber}`,
      birdId: selectedBirdId,
      bandId: birdCertificate.data.bird.identification.bandId || birdCertificate.data.bird.bandNumber,
      shareUrl: certificateShareUrl,
      pdfFilename: filename || undefined,
    });
    if (filename) {
      toast.success('Email drafted — attach the downloaded PDF before sending');
    }
  }, [selectedBirdId, birdCertificate.data, exportCertificatePdf, certificateShareUrl]);

  const renderReportSelector = () => <ReportTypeSelector onSelect={handleReportSelect} />;

  const renderReportHeader = () => (
    <ReportHeader
      selectedReport={selectedReport!}
      selectedReportOption={reportOptions.find((r) => r.id === selectedReport)}
      dateRange={dateRange}
      onDateRangeChange={(value, newFilters) => {
        setDateRange(value);
        setFilters(newFilters);
      }}
      filters={filters}
      onFiltersChange={setFilters}
      onBack={() => {
        setSelectedReport(null);
        setSelectedBirdId(null);
        setSearchParams({}, { replace: true });
      }}
      getDateFilters={getDateFilters}
      shareUrl={selectedReport === 'bird-certificate' ? certificateShareUrl : undefined}
      onExportPDF={
        selectedReport === 'bird-certificate'
          ? async () => {
              if (pdfBusy) return;
              await exportCertificatePdf();
            }
          : undefined
      }
      onEmailShare={
        selectedReport === 'bird-certificate' ? handleCertificateEmailShare : undefined
      }
      reportData={
        selectedReport === 'bird-certificate' ? birdCertificate.data : undefined
      }
    />
  );

  const renderBirdCertificate = () => (
    <BirdCertificateReport
      selectedBirdId={selectedBirdId}
      onBirdSelect={setSelectedBirdId}
      data={birdCertificate.data}
      loading={birdCertificate.loading}
      error={birdCertificate.error}
      onReload={birdCertificate.reload}
      printRef={certificatePrintRef}
      shareUrl={certificateShareUrl}
    />
  );

  const renderBirdsListing = () => (
    <BirdsListingReport
      data={birdsListing.data}
      loading={birdsListing.loading}
      error={birdsListing.error}
      onReload={birdsListing.reload}
      filters={filters}
    />
  );

  const renderBreedingList = () => (
    <BreedingListReport
      data={breedingList.data}
      loading={breedingList.loading}
      error={breedingList.error}
      onReload={breedingList.reload}
    />
  );

  const renderMutationInventory = () => (
    <MutationInventoryReport speciesId={filters.speciesId} />
  );

  const renderBreedingOutcomes = () => <BreedingOutcomesReport />;

  const renderInventoryReport = () => (
    <InventoryReportView
      data={inventoryReport.data}
      loading={inventoryReport.loading}
      error={inventoryReport.error}
      onReload={inventoryReport.reload}
    />
  );

  const renderBalanceSheet = () => (
    <BalanceSheetReport
      data={balanceSheet.data}
      loading={balanceSheet.loading}
      error={balanceSheet.error}
      onReload={balanceSheet.reload}
    />
  );

  const renderIncomeStatement = () => (
    <IncomeStatementReport
      data={incomeStatement.data}
      loading={incomeStatement.loading}
      error={incomeStatement.error}
      onReload={incomeStatement.reload}
    />
  );

  const renderReport = () => {
    switch (selectedReport) {
      case 'bird-certificate':
        return renderBirdCertificate();
      case 'birds-listing':
        return renderBirdsListing();
      case 'breeding-list':
        return renderBreedingList();
      case 'mutation-inventory':
        return renderMutationInventory();
      case 'breeding-outcomes':
        return renderBreedingOutcomes();
      case 'inventory':
        return renderInventoryReport();
      case 'balance-sheet':
        return renderBalanceSheet();
      case 'income-statement':
        return renderIncomeStatement();
      default:
        return (
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <p className="text-slate-600">This report is being implemented...</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div ref={pagePrintRef}>
      {!selectedReport ? (
        renderReportSelector()
      ) : (
        <div>
          {renderReportHeader()}
          <div className="mt-8">{renderReport()}</div>
        </div>
      )}

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-gray-800 { border-color: #1f2937 !important; }
          .print\\:overflow-visible { overflow: visible !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
